import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
} from "@mui/material";
import Layout from "../../layout";
import axios from "axios";
import {
  ConsumptionTrendChart,
  RevenueByMonthChart,
  BillStatusChart,
  TopConsumersChart,
  EventTypesChart,
  WorkOrdersChart,
  StatCard,
} from "./components/ReportsV2Charts";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://backend.voltreum.com/api/v2/reports";

const Overview = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<any>({
    consumptionTrend: [],
    revenueTrend: [],
    billStatus: [],
    topConsumers: [],
    eventSummary: [],
    workOrderSummary: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          consumptionRes,
          revenueRes,
          billStatusRes,
          topConsumersRes,
          eventSummaryRes,
          workOrderSummaryRes,
        ] = await Promise.all([
          axios.get(`${BASE_URL}/consumption-trend`),
          axios.get(`${BASE_URL}/revenue-trend`),
          axios.get(`${BASE_URL}/bill-status`),
          axios.get(`${BASE_URL}/top-consumers?top=8`),
          axios.get(`${BASE_URL}/event-summary`),
          axios.get(`${BASE_URL}/work-order-summary`),
        ]);

        setData({
          consumptionTrend: consumptionRes.data.data,
          revenueTrend: revenueRes.data.data,
          billStatus: billStatusRes.data.data,
          topConsumers: topConsumersRes.data.data,
          eventSummary: eventSummaryRes.data.data,
          workOrderSummary: workOrderSummaryRes.data.data,
        });
      } catch (error) {
        console.error("Error fetching reports data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/reports-v2/overview");
    if (newValue === 1) navigate("/reports-v2/consumption");
    if (newValue === 2) navigate("/reports-v2/revenue-billing");
    if (newValue === 3) navigate("/reports-v2/billing-analytics");
  };

  // Calculate stats for top cards
  const totalConsumption = data.consumptionTrend.reduce(
    (acc: number, item: any) => acc + item.totalKwh,
    0,
  );
  const totalRevenue = data.revenueTrend.reduce(
    (acc: number, item: any) => acc + item.totalRevenue,
    0,
  );
  const activeMeters =
    data.consumptionTrend.length > 0
      ? data.consumptionTrend[data.consumptionTrend.length - 1].meterCount
      : 0;
  const totalEvents = data.eventSummary.reduce(
    (acc: number, item: any) => acc + item.count,
    0,
  );

  return (
    <Layout>
      <Box
        sx={{
          p: 0,
          backgroundColor: "#F3F6FC",
          minHeight: "100vh",
          color: "#181B25",
        }}
      >
        <Box sx={{ mb: 3, pt: 0, px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                minHeight: "48px",
              },
              "& .Mui-selected": { color: "#1064ED !important" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1064ED",
                height: "3px",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Consumption" />
            <Tab label="Revenue & Billing" />
            <Tab label="Billing Analytics" />
            <Tab label="Power Quality" />
            <Tab label="Predictive Analytics" />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, pb: 4 }}>
          <Grid container spacing={3}>
            {/* Stat Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="TOTAL CONSUMPTION"
                value={`${(totalConsumption / 1000).toFixed(1)}K kWh`}
                subValue="Avg 8.7K kWh/day"
                color="#1064ED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="TOTAL REVENUE"
                value={`$${(totalRevenue / 1000000).toFixed(2)}M`}
                subValue="20498 bills generated"
                color="#0D9488"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="ACTIVE METERS"
                value={activeMeters}
                subValue="Connected devices"
                color="#7C3AED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="TOTAL EVENTS"
                value={`${(totalEvents / 1000).toFixed(1)}K`}
                subValue="5 event types"
                color="#EA580C"
              />
            </Grid>

            {/* Consumption Trend */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Consumption Trend
                  </Typography>
                  <ConsumptionTrendChart data={data.consumptionTrend} />
                </CardContent>
              </Card>
            </Grid>

            {/* Revenue by Month and Bill Status */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Revenue by Month
                  </Typography>
                  <RevenueByMonthChart data={data.revenueTrend} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Bill Status
                  </Typography>
                  <BillStatusChart data={data.billStatus} />
                </CardContent>
              </Card>
            </Grid>

            {/* Top 8 Consumers */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Top 8 Consumers
                  </Typography>
                  <TopConsumersChart data={data.topConsumers} />
                </CardContent>
              </Card>
            </Grid>

            {/* Event Types and Work Orders */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Event Types
                  </Typography>
                  <EventTypesChart data={data.eventSummary} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#181B25",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    Work Orders by Status
                  </Typography>
                  <WorkOrdersChart data={data.workOrderSummary} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Layout>
  );
};

export default Overview;
